import { twMerge } from 'tailwind-merge';

import { formatNumber } from '@/utils/format';

import type { AnalyticsBreakdownItem } from '@/features/analytics/analytics.types';

interface BreakdownPanelProps {
  title: string;
  items: AnalyticsBreakdownItem[];
  /** Bar color, expressed as a DaisyUI background utility class. */
  barClassName?: string;
}

/**
 * Renders a labelled horizontal bar breakdown for a list of categories.
 * Each bar width is relative to the largest value in the set.
 */
export function BreakdownPanel(props: BreakdownPanelProps) {
  const barClassName = props.barClassName ?? 'bg-primary';

  const maxCount = props.items.reduce((largest, item) => Math.max(largest, item.count), 0);

  return (
    <div className="card bg-base-100 border border-base-300">
      <div className="card-body gap-4">
        <h2 className="card-title text-base">{props.title}</h2>

        <div className="flex flex-col gap-4">
          {props.items.map((item) => {
            const widthPercentage = maxCount === 0 ? 0 : Math.round((item.count / maxCount) * 100);

            return (
              <div key={item.label} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-base-content/80">{item.label}</span>
                  <span className="font-semibold">{formatNumber(item.count)}</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-base-200">
                  <div
                    className={twMerge('h-2.5 rounded-full', barClassName)}
                    style={{ width: `${widthPercentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
